import { DeliveryState, simulateLatency, type ApiContext } from '../shared';
import { STUB_DELIVERIES } from './stubData';
import type {
  Delivery,
  CreateImmediateDeliveryRequest,
  ListDeliveriesParams,
  ListDeliveriesResponse,
  GetDeliveryResponse,
  CancelDeliveryResponse,
} from './types';
import { apiClient } from '@admin-ui-root/src/app/lib/apiClient';
import { fileDeliveryApiUrl } from '@admin-ui-root/src/app/pages/FileDelivery/api/api';

export const listDeliveries = async (
  ctx: ApiContext,
  params?: ListDeliveriesParams,
): Promise<ListDeliveriesResponse> => {
  const api = apiClient();

  // Build query parameters
  const searchParams = new URLSearchParams();
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params?.pageToken) searchParams.append('pageToken', params.pageToken);
  if (params?.filter) searchParams.append('filter', params.filter);
  if (params?.orderBy) searchParams.append('orderBy', params.orderBy);
  if (params?.parent) searchParams.append('parent', params.parent);

  const queryString = searchParams.toString();
  const url = queryString ? `deliveries?${queryString}` : 'deliveries';

  const response = await api
    .get(fileDeliveryApiUrl(url), {
      headers: {
        'x-fds-profile': ctx.serviceAccount,
        'x-c2fo-dc': ctx.datacenter,
      },
    })
    .json<unknown>();

  // Handle empty object response when no results are found
  if (!response || typeof response !== 'object') {
    return {
      deliveries: [],
      totalSize: 0,
    };
  }

  return response as ListDeliveriesResponse;
};

export const getDelivery = async (ctx: ApiContext, name: string): Promise<Delivery | null> => {
  const api = apiClient();

  try {
    const response = await api
      .get(fileDeliveryApiUrl(name), {
        headers: {
          'x-fds-profile': ctx.serviceAccount,
          'x-c2fo-dc': ctx.datacenter,
        },
      })
      .json<GetDeliveryResponse>();

    return response.delivery;
  } catch (error) {
    // Return null if delivery not found (404) or other errors
    return null;
  }
};

export const createImmediateDelivery = async (
  ctx: ApiContext,
  _request: CreateImmediateDeliveryRequest,
): Promise<Delivery> => {
  // TODO: Replace with real API call when available
  // No-op implementation - returns a mock delivery
  await simulateLatency();

  const newDelivery: Delivery = {
    name: `deliveries/${crypto.randomUUID()}`,
    scheduledDelivery: null,
    sourceLocation: _request.sourceLocation,
    sourcePath: _request.sourcePath,
    targetLocation: _request.targetLocation,
    targetPath: _request.targetPath,
    state: DeliveryState.DELIVERY_STATE_PENDING,
    statusMessage: 'Delivery created',
    fileTransfers: [],
    startTime: new Date().toISOString(),
  };

  return newDelivery;
};

export const cancelDelivery = async (ctx: ApiContext, name: string): Promise<CancelDeliveryResponse> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  const existing = STUB_DELIVERIES.find((d) => d.name === name);
  if (!existing) {
    throw new Error(`Delivery not found: ${name}`);
  }

  return {
    delivery: {
      ...existing,
      state: DeliveryState.DELIVERY_STATE_CANCELLED,
      statusMessage: 'Cancelled by user',
      endTime: new Date().toISOString(),
    },
  };
};
